import React, {
    createContext, useContext, useEffect, useReducer, useMemo, Suspense,
} from 'react';
import Button from '../button';
import RowSet from '../grid/row-set';
import DebugPill from './debug-pill';
import { colors } from '../colors';
import DraftDebug from './draft-debug';
import PageSlugDebug from './page-slug';
import PropsBrowser from './props-browser';

const PageDebug = React.lazy(() => import('./page-debug'));
const GridLinesDebug = React.lazy(() => import('./grid-lines-debug'));
const CenterLineDebug = React.lazy(() => import('./center-line-debug'));

interface DebugContext {
    showBlockNames: boolean
    showTemplateNames: boolean
    showGridLines: boolean
    showCenter: boolean
    showCurrentPublishState: boolean
    showPageSlug: boolean
}

const DebugContext = createContext<DebugContext | undefined>(undefined);

interface DebugProviderProps {
    children: React.ReactNode;
    id: string;
    pageProps: Omit<import('gatsby').PageProps, 'children'>;
    publishedAt?: string | null;
}

const DEBUG_LOCAL_KEY = 'servpro-debug';

const DEBUG_INITIAL_STATE: DebugContext = {
    showBlockNames: false,
    showTemplateNames: false,
    showGridLines: false,
    showCenter: false,
    showCurrentPublishState: false,
    showPageSlug: false,
};

type DebugShowReducer = (state: DebugContext, action: Partial<DebugContext>) => DebugContext;

const debugShowReducer: DebugShowReducer = (state, action) => {
    const update = { ...state, ...action };
    try {
        localStorage.setItem(DEBUG_LOCAL_KEY, JSON.stringify(update));
    } catch (e) {
        // ignore non-working localstorage
    }
    return update;
};

const DebugProvider = ({ children, id, pageProps, publishedAt }: DebugProviderProps): JSX.Element => {
    const shouldRenderWidget = (process.env.NODE_ENV === 'development');

    let pageSlug: string|null = null;

    if ('pageContext' in pageProps) {
        if ('slug' in pageProps.pageContext && typeof pageProps.pageContext.slug === 'string') {
            pageSlug = pageProps.pageContext.slug;
        }
    }

    const [visible, setVisible] = React.useState(false);
    const [show, setShow] = useReducer<DebugShowReducer, Partial<DebugContext>>(
        debugShowReducer,
        {},
        () => DEBUG_INITIAL_STATE,
    );

    // Stable context value
    const contextValue = useMemo(() => show, [show]);

    const update = (key: keyof DebugContext): void => {
        setShow({ [key]: !show[key] });
    };

    useEffect(() => {
        if (shouldRenderWidget) {
            try {
                const localDebug = localStorage.getItem(DEBUG_LOCAL_KEY);
                if (localDebug) {
                    const parsed = JSON.parse(localDebug);
                    if (parsed && typeof parsed === 'object' && Object.keys(parsed).length > 0) {
                        setShow(parsed);
                    }
                }
            } catch (e) {
                // ignore non-working localstorage
            }
        }
    }, [shouldRenderWidget]);

    return (
        <DebugContext.Provider value={contextValue}>
            {shouldRenderWidget && (
                visible ? (
                    <div css={{
                        position: 'fixed',
                        backgroundColor: 'white',
                        width: 'min(200px, 20%)',
                        minHeight: '200px',
                        boxShadow: '0 0 8px #0004',
                        borderRadius: '8px',
                        bottom: '40px',
                        right: '40px',
                        padding: '10px',
                        zIndex: 10000,
                    }}
                    >
                        <div css={{
                            paddingBottom: '10px',
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                            fontSize: '10px',
                        }}
                        >
                            <div>Layout Debug</div>
                            <button
                                type="button"
                                onClick={() => {
                                    setVisible(false);
                                }}
                                css={{
                                    backgroundColor: '#eee',
                                    padding: '3px 6px',
                                    borderRadius: '3px',
                                }}
                            >
                                Close
                            </button>
                        </div>
                        <RowSet breakpoints={{ dt: { between: 2 } }}>
                            {Object.entries(show).map(([key, val]) => (
                                <div key={key}>
                                    <Button
                                        css={{
                                            backgroundColor: val ? 'green' : 'black',
                                            color: '#fff',
                                            padding: '4px 10px',
                                            borderRadius: '3px',
                                            transition: '0.2s',
                                            fontSize: '12px',
                                        }}
                                        onClick={() => {
                                            update(key as keyof DebugContext);
                                        }}
                                    >
                                        {key}
                                        :
                                        {(val as boolean).toString()}
                                    </Button>
                                </div>
                            ))}
                        </RowSet>
                    </div>
                ) : (
                    <div css={{
                        position: 'fixed',
                        bottom: '40px',
                        right: '40px',
                        padding: '4px',
                        fontSize: '10px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        transition: '0.3s',
                        zIndex: 10000,
                    }}
                    >
                        <button
                            type="button"
                            onClick={() => {
                                setVisible(true);
                            }}
                        >
                            <DebugPill
                                css={{
                                    boxShadow: '0 0 8px #0004',
                                }}
                            >
                                Debug
                            </DebugPill>
                        </button>
                        <DebugPill
                            css={{
                                boxShadow: '0 0 8px #0004',
                            }}
                        >
                            <PropsBrowser {...pageProps} />
                        </DebugPill>
                        {id && (
                            <a
                                href={`/api/get-editing-url?id=${id}`}
                                target="_blank"
                                rel="noopener noreferrer"
                            >
                                <DebugPill
                                    css={{
                                        boxShadow: '0 0 8px #0004',
                                        backgroundColor: colors.statesError,
                                    }}
                                >
                                    Edit Page
                                </DebugPill>
                            </a>
                        )}
                    </div>
                )
            )}
            {/* Overlays: always rendered, always inside provider, always get latest context */}
            <Suspense fallback={null}>
                <PageDebug {...pageProps} />
                <GridLinesDebug />
                <CenterLineDebug />
                <DraftDebug publishedAt={publishedAt} />
                {pageSlug && <PageSlugDebug slug={pageSlug} />}
            </Suspense>
            {children}
        </DebugContext.Provider>
    );
};

export default DebugProvider;

export const useDebug = (): DebugContext => {
    const ctx = useContext(DebugContext);
    if (!ctx) {
        throw new Error('useDebug is missing parent DebugProvider.');
    }
    return ctx;
};
